import { withAuthenticationRequired } from '@auth0/auth0-react';
import { Role } from 'consts/auth';
import { isFullAccessRoutePattern } from 'consts/route-access';
import { useHasRole, useIsPLOnly } from 'hooks/use-auth';
import { LoadingPage } from 'pages';
import React from 'react';
import { Redirect, Route, RouteComponentProps, RouteProps } from 'react-router-dom';

// HACK: PL-only users may only land on the Petro-Logistics Shiptracking page.
// This is a frontend-only redirect — backend APIs are not gated. Move to a
// proper backend allowlist + MCP server guard ASAP.
const PL_ONLY_ALLOWED_PATH = '/petrologistics-shiptracking';

interface PrivateRouteProps {
  role?: Role;
}

export function PrivateRoute ({
  component: Component,
  children,
  render,
  role,
  path,
  ...args
}: PrivateRouteProps & RouteProps) {
  // Which routes need a subscription lives in consts/route-access, shared with
  // the sidebar so a nav entry can't disagree with the route it points at.
  // An explicit `role` prop still wins, for one-off gates outside that list.
  const requiredRole =
    role ?? (isFullAccessRoutePattern(path) ? Role.FullAccess : Role.BasicAccess);
  const userHasRole = useHasRole(requiredRole);
  const hasFullAccess = useHasRole(Role.FullAccess);
  const isPLOnly = useIsPLOnly();
  const userCanAccess = userHasRole || hasFullAccess;
  // HACK: PL-only users get bounced off any route that isn't the PL page.
  const blockedForPLOnly = isPLOnly && path !== PL_ONLY_ALLOWED_PATH;

  if (children) {
    throw new Error('children not supported for private routes');
  }

  if (render) {
    throw new Error('render not supported for private routes');
  }

  if (!Component) {
    throw new Error('component to render must be defined');
  }

  // Memoize the protected wrapper ONCE
  const ProtectedComponent = React.useMemo(() => {
    return withAuthenticationRequired<RouteComponentProps>((auth0Props) => {
      // HACK: PL-only short-circuit — sends user to the only page they may use.
      if (blockedForPLOnly) {
        return <Redirect to={PL_ONLY_ALLOWED_PATH} />;
      }
      if (userCanAccess) {
        return <Component {...auth0Props} />;
      }
      // Authenticated but lacking the route's role (free / expired-trial
      // users on a FullAccess route): send them to the Agent rather than the
      // signup pitch — they already have an account, and chat plus the single
      // market-overview watchlist is the whole free-tier surface.
      return <Redirect to="/chat" />;
    }, {
      onRedirecting: () => (
        <LoadingPage message="Redirecting you to the login page..." />
      ),
      returnTo: () => window.location.pathname,
    });
  }, [Component, userCanAccess, blockedForPLOnly]);

  return <Route {...args} path={path} component={ProtectedComponent} />;
}
